import React from 'react';
import { supabase } from './supabase';

export interface Tag {
  id: string;
  name: string;
  film_count?: number;
}

interface TagsContextType {
  tags: Tag[];
  tagsWithCounts: Tag[];
  loading: boolean;
  error: string | null;
}

export const TagsContext = React.createContext<TagsContextType>({
  tags: [],
  tagsWithCounts: [],
  loading: true,
  error: null
});

export function TagsProvider({ children }: { children: React.ReactNode }) {
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [tagsWithCounts, setTagsWithCounts] = React.useState<Tag[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadTags = async () => {
      try {
        // Get all tags
        const { data: tagsData, error: tagsError } = await supabase
          .from('tags')
          .select('id, name')
          .order('name');

        if (tagsError) throw tagsError;
        setTags(tagsData || []);

        // Get tags with counts using the get_tag_counts function
        const { data: tagsWithCountsData, error: countsError } = await supabase
          .rpc('get_tag_counts');

        if (countsError) throw countsError;
        setTagsWithCounts(tagsWithCountsData || []);
      } catch (error) {
        console.error('Error loading tags:', error);
        setError('Failed to load tags');
      } finally {
        setLoading(false);
      }
    };

    loadTags();
  }, []);

  return (
    <TagsContext.Provider value={{ tags, tagsWithCounts, loading, error }}>
      {children}
    </TagsContext.Provider>
  );
}

export function useTags() {
  return React.useContext(TagsContext);
}